import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { SEO } from '@/components/SEO';
import { ROUTE_META } from '@/seo/config';
import { eventsJsonLd } from '@/seo/jsonld';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Calendar } from '@/components/ui/calendar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/i18n';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  Calendar as CalendarIcon, 
  MapPin, 
  Clock,
  Plus,
  Filter,
  List,
  Grid,
  ChevronLeft,
  ChevronRight,
  Users,
  Loader2
} from 'lucide-react';
import { format, isSameDay, addMonths, subMonths, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isToday } from 'date-fns';
import { fr } from 'date-fns/locale';
import BottomNav from '@/components/BottomNav';

// Communes de La Réunion
const COMMUNES = [
  "Toutes", "Saint-Denis", "Saint-Paul", "Saint-Pierre", "Le Tampon", "Saint-André",
  "Saint-Louis", "Le Port", "Saint-Benoît", "Saint-Joseph", "Sainte-Marie",
  "Sainte-Suzanne", "Saint-Leu", "La Possession", "L'Étang-Salé", "Petite-Île",
  "Bras-Panon", "Les Avirons", "Trois-Bassins", "Salazie", "Cilaos",
  "Entre-Deux", "Saint-Philippe", "La Plaine-des-Palmistes", "Sainte-Rose"
];

const CalendarPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  const { language } = useLanguage();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [displayMonth, setDisplayMonth] = useState(new Date());
  const [viewMode, setViewMode] = useState('calendar'); // 'calendar' or 'list'
  const [selectedCommune, setSelectedCommune] = useState('Toutes');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [displayMonth]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      const response = await api.get('/posts', {
        params: { category: 'evenement' }
      });
      // Filter events that have an event_date
      const eventsWithDate = response.data.filter(post => post.event_date);
      setEvents(eventsWithDate);
    } catch (error) {
      toast.error(language === 'cr' ? 'Erèr sharzman' : 'Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Filter events by commune
  const filteredEvents = useMemo(() => {
    if (selectedCommune === 'Toutes') return events;
    return events.filter(event => event.commune === selectedCommune);
  }, [events, selectedCommune]);

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return filteredEvents.filter(event => {
      const eventDate = new Date(event.event_date);
      return isSameDay(eventDate, date);
    });
  };

  // Get all dates that have events
  const eventDates = filteredEvents.map(event => new Date(event.event_date));

  // Events for selected date
  const selectedDateEvents = getEventsForDate(selectedDate);

  // Upcoming events (sorted)
  const upcomingEvents = useMemo(() => {
    const now = new Date();
    return filteredEvents
      .filter(event => new Date(event.event_date) >= now)
      .sort((a, b) => new Date(a.event_date) - new Date(b.event_date));
  }, [filteredEvents]);

  // Events grouped by month for list view
  const eventsByMonth = useMemo(() => {
    const grouped = {};
    upcomingEvents.forEach(event => {
      const monthKey = format(new Date(event.event_date), 'MMMM yyyy', { locale: fr });
      if (!grouped[monthKey]) grouped[monthKey] = [];
      grouped[monthKey].push(event);
    });
    return grouped;
  }, [upcomingEvents]);

  // Modifiers for calendar
  const modifiers = {
    hasEvent: eventDates
  };

  const modifiersStyles = {
    hasEvent: {
      backgroundColor: 'hsl(var(--primary) / 0.15)',
      borderRadius: '50%',
      fontWeight: 'bold'
    }
  };

  // Navigate months
  const goToPreviousMonth = () => setDisplayMonth(subMonths(displayMonth, 1));
  const goToNextMonth = () => setDisplayMonth(addMonths(displayMonth, 1));
  const goToToday = () => {
    setDisplayMonth(new Date());
    setSelectedDate(new Date());
  };

  // Event Card Component
  const EventCard = ({ event, compact = false }) => (
    <Card 
      className="cursor-pointer hover:bg-muted/50 transition-colors overflow-hidden group"
      onClick={() => navigate(`/post/${event.post_id}`)}
      data-testid={`event-card-${event.post_id}`}
    >
      <CardContent className={compact ? "p-3" : "p-4"}>
        <div className="flex gap-3">
          {event.image_url && !compact && (
            <img 
              src={event.image_url}
              alt={event.title}
              className="w-20 h-20 object-cover rounded-lg flex-shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <h3 className={`font-bold ${compact ? 'text-sm' : 'text-base'} line-clamp-1 group-hover:text-primary transition-colors`}>
                {event.title}
              </h3>
              {!compact && (
                <Badge variant="outline" className="flex-shrink-0 text-xs">
                  {format(new Date(event.event_date), 'd MMM', { locale: fr })}
                </Badge>
              )}
            </div>
            {!compact && (
              <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                {event.content}
              </p>
            )}
            <div className={`flex flex-wrap gap-2 ${compact ? 'mt-1' : 'mt-2'} text-xs text-muted-foreground`}>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {format(new Date(event.event_date), 'HH:mm', { locale: fr })}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {event.commune}
              </span>
              {event.likes_count > 0 && (
                <span className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  {event.likes_count}
                </span>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="min-h-screen tropic-bg pb-28 md:pb-12 md:pt-16">
      {events.length > 0 && <SEO {...ROUTE_META['/calendar']} path="/calendar" jsonLd={eventsJsonLd(events)} />}
      {/* Header */}
      <header className="sticky top-0 md:top-16 z-40 glass border-b">
        <div className="container mx-auto max-w-4xl px-4 py-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(-1)}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <h1 className="text-xl font-bold">
                {language === 'cr' ? 'Kalandryé' : 'Calendrier'}
              </h1>
              <p className="text-xs text-muted-foreground">
                {language === 'cr' ? 'Événman La Rényon' : 'Événements à La Réunion'}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline"
                size="icon"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'bg-primary/10' : ''}
                data-testid="toggle-filters-btn"
              >
                <Filter className="h-4 w-4" />
              </Button>
              <Button 
                onClick={() => navigate('/create')}
                size="sm"
                data-testid="create-event-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                <span className="hidden sm:inline">
                  {language === 'cr' ? 'Événman' : 'Événement'}
                </span>
              </Button>
            </div>
          </div>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 flex flex-wrap gap-3">
              <Select value={selectedCommune} onValueChange={setSelectedCommune}>
                <SelectTrigger className="w-48" data-testid="commune-filter">
                  <MapPin className="h-4 w-4 mr-2" />
                  <SelectValue placeholder="Commune" />
                </SelectTrigger>
                <SelectContent>
                  {COMMUNES.map(commune => (
                    <SelectItem key={commune} value={commune}>{commune}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex rounded-lg border overflow-hidden">
                <Button
                  variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-none"
                  onClick={() => setViewMode('calendar')}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'}
                  size="sm"
                  className="rounded-none"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto max-w-4xl px-4 py-6">
        {viewMode === 'calendar' ? (
          <>
            {/* Calendar View */}
            <Card className="mb-6">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <Button variant="ghost" size="icon" onClick={goToPreviousMonth}>
                    <ChevronLeft className="h-5 w-5" />
                  </Button>
                  <div className="text-center">
                    <h2 className="font-bold text-lg capitalize">
                      {format(displayMonth, 'MMMM yyyy', { locale: fr })}
                    </h2>
                    <Button 
                      variant="link" 
                      className="text-xs p-0 h-auto"
                      onClick={goToToday}
                    >
                      {language === 'cr' ? 'Zordi' : "Aujourd'hui"}
                    </Button>
                  </div>
                  <Button variant="ghost" size="icon" onClick={goToNextMonth}>
                    <ChevronRight className="h-5 w-5" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-4 pt-0">
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={(date) => date && setSelectedDate(date)}
                  month={displayMonth}
                  onMonthChange={setDisplayMonth}
                  locale={fr}
                  modifiers={modifiers}
                  modifiersStyles={modifiersStyles}
                  className="rounded-md border-0 w-full"
                  classNames={{
                    caption: "hidden",
                    nav: "hidden"
                  }}
                  data-testid="events-calendar"
                />
              </CardContent>
            </Card>

            {/* Events for selected date */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold capitalize">
                  {format(selectedDate, 'EEEE d MMMM', { locale: fr })}
                </h2>
                <Badge variant={selectedDateEvents.length > 0 ? "default" : "outline"}>
                  {selectedDateEvents.length} {language === 'cr' ? 'événman' : 'événement'}
                  {selectedDateEvents.length !== 1 ? 's' : ''}
                </Badge>
              </div>

              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : selectedDateEvents.length === 0 ? (
                <div className="text-center py-12 bg-muted/30 rounded-xl">
                  <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">
                    {language === 'cr' ? 'Nana pa événman' : 'Aucun événement ce jour'}
                  </p>
                  <Button 
                    variant="outline" 
                    className="mt-4"
                    onClick={() => navigate('/create')}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {language === 'cr' ? 'Kré in événman' : 'Créer un événement'}
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedDateEvents.map((event) => (
                    <EventCard key={event.post_id} event={event} />
                  ))}
                </div>
              )}
            </div>
          </>
        ) : (
          /* List View */
          <div className="space-y-6">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : upcomingEvents.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-xl">
                <CalendarIcon className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {language === 'cr' ? 'Nana pa événman a venir' : 'Aucun événement à venir'}
                </p>
              </div>
            ) : (
              Object.entries(eventsByMonth).map(([month, monthEvents]) => (
                <div key={month}>
                  <h2 className="text-lg font-bold capitalize mb-3 sticky top-0 bg-background py-2">
                    {month}
                  </h2>
                  <div className="space-y-3">
                    {monthEvents.map((event) => (
                      <EventCard key={event.post_id} event={event} />
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Upcoming Events Carousel (only in calendar view) */}
        {viewMode === 'calendar' && upcomingEvents.length > 0 && (
          <div className="mt-8">
            <h2 className="text-lg font-bold mb-4">
              {language === 'cr' ? 'Proshin événman' : 'Prochains événements'}
            </h2>
            <ScrollArea className="w-full">
              <div className="flex gap-4 pb-4">
                {upcomingEvents.slice(0, 10).map((event) => (
                  <Card 
                    key={event.post_id}
                    className="w-64 flex-shrink-0 cursor-pointer hover:bg-muted/50 transition-colors"
                    onClick={() => navigate(`/post/${event.post_id}`)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge className="badge-evenement">
                          {format(new Date(event.event_date), 'd MMM', { locale: fr })}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {format(new Date(event.event_date), 'HH:mm')}
                        </span>
                      </div>
                      <h4 className="font-bold line-clamp-2">{event.title}</h4>
                      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1">
                        <MapPin className="h-3 w-3" />
                        {event.commune}
                      </p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </main>

      <BottomNav />
    </div>
  );
};

export default CalendarPage;
